/* event handlers, state, and state management for pointer events */

import app from '../application-container';
import {
    Intersection,
    Vector2,
} from 'three';
import {
    suppress_animate,
    unsuppress_animate_if_ready,
    update_dom_text_values_and_coordinates,
} from '../utility';
import {
    MouseButton,
    get_normalized_mouse_coordinates,
    is_button_pressed,
    is_this_button_pressed,
    mousedown_handler,
    mousemove_handler,
    mouseup_handler,
} from './mouse';
import { from_pointer, point_point_distance } from '@phi/maths/vector2';
import { set_shift } from '../keypress';
import { touch_down, touch_move, touch_up } from './touch';
//import { re_indicate_current_vertices, selected_coordinates_visible } from '@features/indicate-vertices';

//////////////////////////////
// STATE
//////////////////////////////

/** how long before a press is considered a click, in ms */
const click_delay = 250;

const click_sensitivity_factor = 1;

/** how far before a press is considered a click, in px */
const small_click_threshold = 3;
const large_click_threshold = 24;

/** whether we are currently performing a hold-and-drag operation */
let is_dragging = false;

/** XY position of last pressed pixel on the screen */
export const initial_pressed_coords = new Vector2(0, 0);

/** timestamp of most recent pressed pixel on the screen */
let last_pressed_time = 0;

/** The normalized coordinates of the pointer on the screen.
 * Constantly updated inside move event.
 * Keep in mind that this could be the average of multiple pointers (many fingers)
 */
export const pointer_normalised = new Vector2(0, 0);

export const pointer_normalised_prev = new Vector2(0, 0);

//////////////////////////////
// STATE HANDLERS
//////////////////////////////

export function update_pointer_normalised(e: MouseEvent | PointerEvent) {
    const fresh = get_normalized_mouse_coordinates(e);
    pointer_normalised.x = fresh.x;
    pointer_normalised.y = fresh.y;
}

export function is_pointer_normalised_prev_initialised() {
    return (
        pointer_normalised_prev.x !== 0
        || pointer_normalised_prev.y !== 0
    );
}

export function update_cursor_press_time_and_position(e: MouseEvent | PointerEvent) {
    last_pressed_time = performance.now();
    initial_pressed_coords.x = e.clientX;
    initial_pressed_coords.y = e.clientY;
}

export function time_since_last_press() {
    return performance.now() - last_pressed_time;
}

export function distance_since_initial_press(e: MouseEvent | PointerEvent) {
    return point_point_distance(from_pointer(e), initial_pressed_coords);
}

export function is_click(e: PointerEvent, long = false) {
    const time_factor = long ? 20 : click_sensitivity_factor;
    return time_since_last_press() < click_delay * time_factor && !pointer_has_moved_a_little(e);
}

export function pointer_has_moved_a_little(e: PointerEvent): boolean {
    const factor = e.pointerType === 'touch' ? 2 : 1;
    return distance_since_initial_press(e) > (small_click_threshold * factor);
}

export function pointer_has_moved_a_lot(e: PointerEvent): boolean {
    return distance_since_initial_press(e) > large_click_threshold;
}

/** Check whether the distance threshold for a press has elapsed. If not, discard this movement */
export function has_moved_enough_for_drag(e: PointerEvent) {
    if (is_dragging)
        return true;
    else if (!is_button_pressed())
        return true;
    else if (!is_this_button_pressed(MouseButton.primary))
        return true;
    else if (is_click(e))
        return false;
    else if (!pointer_has_moved_a_little(e))
        return false;
    else
        return is_dragging = true;
}


//////////////////////////////
// UTILITIES
//////////////////////////////

export function intersection_to_uid_object(x: Intersection) {
    const o3d = uid_raynode.getObjectById(x.object.id);
    if (!o3d)
        return undefined;
    return uid_object3dIDToUid_object.getItem(o3d.id);
}

const can_safely_run = () => app.state.loaded;

//////////////////////////////
// EVENT HANDLERS
//////////////////////////////

/** called every time the pointer moves
 *
 * handles mouse, finger, and pen
 */
export function pointer_move(e: PointerEvent) {
    if (!can_safely_run())
        return;

    // Check whether the shift key is still pressed. We do this because when we come back from screen grabbing we may have lost a
    // shift keyup event.
    set_shift(e.shiftKey);

    // Move coordinates
    update_dom_text_values_and_coordinates();

    suppress_animate();

    switch (e.pointerType) {
        case 'mouse':
            mousemove_handler(e);
            break;

        case 'touch':
            touch_move(e);
            break;
    }

    unsuppress_animate_if_ready(false);
}

export function pointer_down(e: PointerEvent) {
    if (!can_safely_run())
        return;

    // Use timeout mechanism to distinguish click-drag from click
    is_dragging = false;

    switch (e.pointerType) {
        case 'mouse':
            mousedown_handler(e);
            break;

        case 'touch':
            touch_down(e);
            break;
    }
}

export function pointer_up(e: PointerEvent) {
    if (!can_safely_run())
        return;

    switch (e.pointerType) {
        case 'mouse':
            mouseup_handler(e);
            break;

        case 'touch':
            touch_up(e);
            break;
    }
}
